import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Newspaper, Calendar, FileText, Users, CreditCard,
  Image, DollarSign, Mail, AlertTriangle, LogOut, X, History, Settings, Building2
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Menu items
  const menuItems = [
    { title: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { title: 'News', path: '/admin/news', icon: Newspaper },
    { title: 'Events', path: '/admin/events', icon: Calendar },
    { title: 'Event Registrations', path: '/admin/event-registrations', icon: FileText },
    { title: 'Services', path: '/admin/services', icon: Settings },
    { title: 'Companies', path: '/admin/companies', icon: Building2 },
    { title: 'Members', path: '/admin/members', icon: Users },
    { title: 'Membership Plans', path: '/admin/membership-plans', icon: CreditCard },
    { title: 'Gallery', path: '/admin/gallery', icon: Image },
    { title: 'Payments', path: '/admin/payments', icon: DollarSign },
    { title: 'Contact Enquiries', path: '/admin/contact-submissions', icon: Mail },
    { title: 'Grievances', path: '/admin/grievances', icon: AlertTriangle },
    { title: 'Activity Logs', path: '/admin/audit-logs', icon: History }
  ];

  const handleLogout = () => {
    localStorage.removeItem('ifm_admin_token');
    localStorage.removeItem('ifm_admin_logged_in');
    localStorage.removeItem('ifm_admin_login_time');
    localStorage.removeItem('ifm_admin_username');
    localStorage.removeItem('ifm_admin_profile');
    navigate('/admin/login');
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[998] backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      
      <div className={`
        fixed top-0 left-0 h-full bg-gradient-to-b from-gray-800 to-gray-900 text-white
        ${isMobile ? 'w-[280px]' : 'w-[250px]'}
        ${isMobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        transition-transform duration-300 ease-in-out z-[999]
        shadow-2xl flex flex-col overflow-hidden
      `}>
        {/* Header */}
        <div className="px-5 py-6 border-b border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="bg-primary-600 rounded-lg p-2">
                <LayoutDashboard className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">IFM Admin</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {localStorage.getItem('ifm_admin_username') || 'Admin'}
                </p>
              </div>
            </div>
            {isMobile && (
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-300" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <button
                key={index}
                onClick={() => handleMenuClick(item.path)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-all duration-200 text-left
                  ${isActive 
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/20' 
                    : 'text-gray-300 hover:bg-gray-700 hover:text-black'
                  }
                `}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${isActive ? 'font-semibold' : 'font-normal'}`}>
                  {item.title}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-gray-700">
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white hover:text-black rounded-lg transition-colors duration-200 font-medium text-sm shadow-lg shadow-red-600/20"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
